import h from 'hyperscript';

import { getLogger } from 'cadenza/utils/logging';
import { defaultXhrErrorHandler } from 'cadenza/api-client/api';
import { HEADER_DISY_CANCEL_ID, HEADER_REQUESTED_WITH } from 'cadenza/api-client/api-constants';
import { on } from 'cadenza/utils/event-util';

const LOGGER = getLogger('cadenza/utils/download');

// INFO level (also in production), because blob downloads
// do not show up in the devtools' network tab.
LOGGER.setLevel(LOGGER.levels.INFO);

/**
 * Loads a file via XHR from the given URL and using the given options.
 * The file is downloaded entirely as a blob before the browser's download action is triggered
 * via an invisible anchor.
 *
 * Use this function if you need to...
 * - set request headers (e.g. Accept) or change the request method (e.g. to HEAD).
 * - know when the download is finished or whether it was successful.
 * - specify the download filename.
 *
 * Otherwise use {@link downloadImmediately}.
 *
 * Example usage:
 *
 * download(url);
 * download(url, {
 *   method: 'GET',
 *   headers: {
 *     'Accept': 'application/pdf', // What we'd like to get.
 *     'Authorization': 'Your auth key'
 *   },
 *   responseType: 'blob',
 *   filename: 'Over'
 * });
 *
 * @param {string} url - The URL of the file to download
 * @param {object} options - Options for the XHR request
 * @return {promise} a Promise that resolves when the file is downloaded
 */
export function download(url, options = {}) {
  // Allow options override
  options = Object.assign(
    {
      method: 'GET',
      headers: {},
      responseType: 'blob',
      filename: undefined,
      defaultFilename: url.split('/').pop(),
      isCanceled: () => false,
    },
    options,
  );

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    if (options.abortSignal) {
      on(options.abortSignal, 'abort', () => xhr.abort(), { once: true });

      if (options.abortSignal.id) {
        options.headers = Object.assign(options.headers || {}, { [HEADER_DISY_CANCEL_ID]: options.abortSignal.id });
      }
    }

    xhr.open(options.method, url, true);
    // required for XHR check on server
    xhr.setRequestHeader(HEADER_REQUESTED_WITH, 'XMLHttpRequest');
    xhr.responseType = options.responseType;
    Object.entries(options.headers).forEach(([header, value]) => {
      xhr.setRequestHeader(header, value);
    });

    xhr.onload = function () {
      if (xhr.status === 200) {
        const filename = options.filename ?? getFilename(xhr) ?? options.defaultFilename;
        LOGGER.info(`Downloading "${filename}" as a blob …`);
        if (!options.isCanceled()) {
          downloadObject(xhr.response, filename);
        }

        resolve(filename);
      } else {
        defaultXhrErrorHandler(xhr, reject);
      }
    };

    xhr.send();
  });
}

// https://stackoverflow.com/a/40940790
function getFilename(xhr) {
  return xhr
    .getResponseHeader('Content-Disposition')
    ?.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)?.[1]
    ?.replace(/['"]/g, '');
}

/**
 * Allows user to download from an object (i.e. Blob, File or MediaSource).
 * Takes care of revoking the object URL to avoid memory leaks.
 * @param {Blob | File | MediaSource} obj The object to download
 * @param {string} [fileName] Default file name to be proposed to the user
 */
export function downloadObject(obj, fileName) {
  const objectUrl = URL.createObjectURL(obj);
  try {
    downloadImmediately(objectUrl, fileName);
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

/**
 * Downloads the file from the given URL, without previously loading the whole file in a Blob.
 *
 * This allows the user to see the download progress via the browser's UI.
 *
 * It might be preferable for big files since the user can manage the download via the browser's UI.
 * It provides a more consistent and familiar UI.
 *
 * It's also recommended for client-side generated content like object URLs and data URLs.
 *
 * However, it allows much less control over the HTTP request than the other download function.
 *
 * @param {string} url - The URL of the file to download
 * @param {string} [fileName] Default file name to be proposed to the user
 */
export function downloadImmediately(url, fileName = '') {
  // create and attach a hidden anchor for the URL
  const a = h('a', {
    href: url,
    // this attribute makes sure the content is downloaded and no navigation occurs to the URL
    // and sets the default file name proposed in the save dialog
    download: fileName,
    hidden: true,
  });
  document.body.appendChild(a);

  // trigger the download
  a.click();

  // remove the anchor
  a.remove();
}
